import React, { useState, useRef, useEffect } from 'react';
import {
  ChevronDownIcon,
  ClipboardDocumentListIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  AdjustmentsHorizontalIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import { Block, ChiffrageView, DecompositionMode, isPrestation, PrixReliability } from '../../types';
import { EditableField, BlockActionsComponent, getPrixOriginState, getPrixOriginColor, FPLabel } from '../atoms';
import {
  BlockCalculationService,
  DecompositionCalculationService,
  DecompositionParams,
} from '../../services';
import PuSourcesModal from './PuSourcesModal';
import VentilationRowPanel from './VentilationRowPanel';
import DecompositionInlineSubbar from './DecompositionInlineSubbar';

export interface LotEventItem {
  id: string;
  name: string;
  description?: string;
  pointed: boolean;
}

interface LotEventBadgeProps {
  events: LotEventItem[];
  onPointEvent?: (eventId: string) => void;
  onOpenCreateEvent?: () => void;
}

const LotEventBadge: React.FC<LotEventBadgeProps> = ({ events, onPointEvent, onOpenCreateEvent }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const pendingCount = events.filter(e => !e.pointed).length;
  const allPointed = pendingCount === 0;

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} className="relative flex-shrink-0">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
        className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded text-xs font-semibold transition-colors ${
          pendingCount > 0
            ? 'bg-orange-100 text-orange-600 hover:bg-orange-200'
            : 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200'
        }`}
        title={pendingCount > 0 ? `${pendingCount} pointage${pendingCount !== 1 ? 's' : ''} en attente` : 'Tous les pointages à jour'}
      >
        {pendingCount > 0 ? pendingCount : '✓'}
      </button>

      {open && (
        <div
          className="absolute left-0 top-full mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-xl z-50"
          onClick={e => e.stopPropagation()}
        >
          <div className="px-3 py-2 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Événements
          </div>
          {allPointed ? (
            <div className="px-3 py-4 text-sm text-emerald-600 text-center">
              Tous les événements sont à jour ✅
            </div>
          ) : (
            <div className="max-h-52 overflow-y-auto divide-y divide-gray-50">
              {events.map(event => (
                <div
                  key={event.id}
                  className={`px-3 py-2.5 flex items-start gap-2 ${event.pointed ? 'opacity-40' : ''}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-800 leading-tight">{event.name}</div>
                    {event.description && (
                      <div className="text-xs text-gray-400 mt-0.5 line-clamp-2">{event.description}</div>
                    )}
                  </div>
                  {event.pointed ? (
                    <span className="flex-shrink-0 text-xs text-emerald-600 mt-0.5">✓</span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onPointEvent?.(event.id)}
                      className="flex-shrink-0 px-2 py-1 rounded text-xs font-medium bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors whitespace-nowrap"
                    >
                      Pointer ✓
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
          <div className="px-3 py-2 border-t border-gray-100">
            <button
              type="button"
              onClick={() => { setOpen(false); onOpenCreateEvent?.(); }}
              className="text-xs text-violet-600 hover:underline"
            >
              + Nouvel événement
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── CoefField ─────────────────────────────────────────────────────────────
// Input numérique compact pour la colonne Coef. Vide = défaut global (affiché
// en placeholder gris). Saisi = override (affiché plus marqué). Vider le champ
// → reset vers le défaut.
interface CoefFieldProps {
  value: number | undefined;
  defaultValue: number | undefined;
  onChange: (raw: string) => void;
}

const CoefField: React.FC<CoefFieldProps> = ({ value, defaultValue, onChange }) => {
  const [draft, setDraft] = useState<string>(value !== undefined ? String(value) : '');
  useEffect(() => {
    setDraft(value !== undefined ? String(value) : '');
  }, [value]);

  const hasOverride = value !== undefined;
  const defaultLabel = defaultValue !== undefined ? String(defaultValue) : '1';
  const tooltip = hasOverride
    ? `Coef override : ${value} — vider le champ pour revenir au défaut ${defaultLabel}`
    : `Coef par défaut : ${defaultLabel} — saisir pour override cette ligne`;

  return (
    <input
      type="number"
      step="0.01"
      min="0"
      value={draft}
      placeholder={defaultLabel}
      onChange={e => setDraft(e.target.value)}
      onBlur={() => onChange(draft)}
      onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
      title={tooltip}
      className={`w-full px-1 py-0.5 text-xs text-center tabular-nums bg-transparent border-0 outline-none focus:ring-1 focus:ring-violet-300 rounded placeholder:text-gray-300 ${
        hasOverride ? 'text-gray-800 font-medium' : 'text-gray-400'
      }`}
    />
  );
};

const RELIABILITY_DOT: Record<PrixReliability, { dot: string; label: string; reason: string }> = {
  high: {
    dot: 'bg-green-500',
    label: 'Fiable',
    reason: 'Nombreuses références · Écarts maîtrisés · Données récentes',
  },
  medium: {
    dot: 'bg-orange-400',
    label: 'Incertain',
    reason: 'Références limitées · Écarts modérés · Données partiellement récentes',
  },
  low: {
    dot: 'bg-red-500',
    label: 'Douteux',
    reason: 'Peu de références · Forts écarts de prix · Données trop anciennes',
  },
};

interface BlockRowProps {
  block: Block;
  isSelected: boolean;
  isEditing: boolean;
  isDragging: boolean;
  allBlocks?: Block[];
  onSelect: (id: string) => void;
  onEdit: (id: string) => void;
  onUpdate: (id: string, updates: Partial<Block>) => void;
  onDelete: (id: string) => void;
  onDragStart?: (id: string, event: React.MouseEvent<Element>) => void;
  onDragEnd?: () => void;
  onStopEditing: () => void;
  onToggleSelection?: (id: string, shiftKey: boolean) => void;
  onToggleCollapse?: (id: string) => void;
  consultationInfo?: { name: string; id: string; status?: string; retainedCompanyName?: string } | null;
  onOpenConsultation?: (consultationId: string) => void;
  isVentilationExpanded?: boolean;
  onToggleVentilation?: (id: string) => void;
  onVentilationBlocksChange?: (blocks: Block[]) => void;
  isDecompositionExpanded?: boolean;
  onToggleDecomposition?: (id: string) => void;
  decompositionParams?: DecompositionParams;
  /** Vue active : 'client' masque le détail F/MO (vue export) ; 'ventilee' l'affiche. */
  chiffrageView?: ChiffrageView;
  lotEvents?: LotEventItem[];
  onPointEvent?: (eventId: string) => void;
  onOpenCreateEvent?: () => void;
}

export const BlockRow: React.FC<BlockRowProps> = ({
  block,
  isSelected,
  isEditing,
  isDragging,
  allBlocks,
  onSelect,
  onEdit,
  onUpdate,
  onDelete,
  onDragStart,
  onDragEnd,
  onStopEditing,
  onToggleSelection,
  onToggleCollapse,
  consultationInfo,
  onOpenConsultation,
  isVentilationExpanded = false,
  onToggleVentilation,
  onVentilationBlocksChange,
  isDecompositionExpanded = false,
  onToggleDecomposition,
  decompositionParams,
  chiffrageView = 'client',
  lotEvents,
  onPointEvent,
  onOpenCreateEvent,
}) => {
  const [showPuModal, setShowPuModal] = useState(false);

  const hasCostLines = allBlocks
    ? allBlocks.some(b => b.parentId === block.id && b.content.isVentilationCostLine)
    : false;

  // Cette ligne est-elle une ligne de coût ventilée (sous une prestation parente) ?
  // Détermine son style d'appartenance (bg + ↳ prefix) en vue ventilée/détaillée.
  const isCostLine = !!block.content.isVentilationCostLine;

  const rawDecompositionMode: DecompositionMode = isPrestation(block)
    ? (block.content.decompositionMode ?? 'forfaitaire')
    : 'forfaitaire';
  // Mutex ventilation / décomposition : quand la prestation parente est ventilée,
  // la décomposition descend au niveau des lignes filles — le parent redevient
  // « forfaitaire » côté affichage (son PU reflète la somme des lignes ventilées
  // ou le forfait du MOE). On conserve la valeur persistée pour pouvoir la
  // restaurer si l'utilisateur dé-ventile.
  const decompositionMode: DecompositionMode = hasCostLines ? 'forfaitaire' : rawDecompositionMode;
  const isDetaillee = decompositionMode !== 'forfaitaire';

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit(block.id);
  };

  const handleFieldSave = (fieldName: string, value: string) => {
    if (isPrestation(block)) {
      const contentUpdate: typeof block.content = { ...block.content, [fieldName]: value };

      // Mode détaillé : PU/Total sont calculés, on bloque les saisies directes
      // et on recalcule automatiquement quand la quantité change.
      if (isDetaillee && (fieldName === 'prixUnitaire' || fieldName === 'prixTotal')) {
        return;
      }
      if (isDetaillee && fieldName === 'quantite' && decompositionParams) {
        const recomputed = DecompositionCalculationService.applyBreakdownToContent(
          { ...block.content, quantite: value },
          value,
          decompositionParams,
        );
        onUpdate(block.id, { content: recomputed });
        return;
      }

      if (fieldName === 'prixUnitaire' && value !== block.content.prixUnitaire) {
        if (block.content.prixUnitaireSource !== 'user') {
          contentUpdate.prixUnitaireOriginal = block.content.prixUnitaire;
        }
        contentUpdate.prixUnitaireSource = 'user';
        contentUpdate.prixUnitaireReliability = undefined;
        contentUpdate.prixUnitaireExpertCurated = undefined;
      }
      if (fieldName === 'quantite' || fieldName === 'prixUnitaire') {
        const qty = BlockCalculationService.parseQuantity(
          fieldName === 'quantite' ? value : (block.content.quantite ?? '1')
        );
        const pu = BlockCalculationService.parsePrice(
          fieldName === 'prixUnitaire' ? value : (block.content.prixUnitaire ?? '0')
        );
        const coef = DecompositionCalculationService.resolveCoef(
          block.content,
          { coefDefaut: decompositionParams?.coefDefaut },
        );
        contentUpdate.prixTotal = BlockCalculationService.formatPrice(qty * pu * coef);
        contentUpdate.prixTotalManual = false;
      }
      if (fieldName === 'prixTotal') {
        contentUpdate.prixTotalManual = true;
      }

      // Auto-transition vers titre : uniquement pour les blocs sans blockType explicite
      // (rétrocompatibilité). Les blocs avec blockType: 'prestation' restent prestations.
      if (!block.blockType) {
        const remainingPU    = fieldName === 'prixUnitaire' ? value : block.content.prixUnitaire;
        const remainingQty   = fieldName === 'quantite'     ? value : block.content.quantite;
        const remainingUnite = fieldName === 'unite'         ? value : block.content.unite;
        const remainingIntit = fieldName === 'intitule'      ? value : block.content.intitule;

        if (!remainingPU && !remainingQty && !remainingUnite && !remainingIntit) {
          contentUpdate.titre = block.content.titre || '';
          contentUpdate.intitule = '';
          contentUpdate.prixTotal = undefined;
          contentUpdate.prixTotalManual = false;
        } else if (!remainingPU && !remainingQty && !remainingUnite && remainingIntit) {
          contentUpdate.titre = remainingIntit;
          contentUpdate.intitule = '';
          contentUpdate.prixTotal = undefined;
          contentUpdate.prixTotalManual = false;
        }
      }

      onUpdate(block.id, { content: contentUpdate });
    }
  };

  const handleFieldCancel = () => {};

  /**
   * Saisie du coef ligne (colonne Coef). Vide = reset vers le défaut global.
   * - Détaillé : delegate à applyBreakdownToContent (intègre PU vente + appro + coef).
   * - Forfaitaire : recalcule total = PU × coef × qty et clear prixTotalManual.
   */
  const handleCoefChange = (raw: string) => {
    if (!isPrestation(block)) return;
    const trimmed = raw.trim();
    const parsed = trimmed === '' ? undefined : parseFloat(trimmed.replace(',', '.'));
    const coefLigne = (parsed !== undefined && !Number.isNaN(parsed) && parsed >= 0)
      ? parsed
      : undefined;
    if (coefLigne === block.content.coefLigne) return;

    const next: typeof block.content = { ...block.content, coefLigne };

    if (isDetaillee && decompositionParams) {
      const recomputed = DecompositionCalculationService.applyBreakdownToContent(
        next,
        block.content.quantite,
        decompositionParams,
      );
      onUpdate(block.id, { content: recomputed });
      return;
    }

    const qty = BlockCalculationService.parseQuantity(block.content.quantite ?? '1');
    const pu = BlockCalculationService.parsePrice(block.content.prixUnitaire ?? '0');
    const coef = DecompositionCalculationService.resolveCoef(
      next,
      { coefDefaut: decompositionParams?.coefDefaut },
    );
    const prixTotal = BlockCalculationService.formatPrice(qty * pu * coef);
    onUpdate(block.id, {
      content: { ...next, prixTotal, prixTotalManual: false },
    });
  };

  const handleDragStart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onDragStart?.(block.id, e);
  };

  const handleDragHandleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const getBlockStyles = () => {
    if (isDragging) {
      return "group flex items-center border-b border-gray-100 bg-blue-50 opacity-75 transition-colors";
    }

    if (isSelected) {
      return "group flex items-center border-b border-gray-100 bg-violet-50 transition-colors";
    }

    if (!isPrestation(block)) {
      return "group flex items-center border-t border-gray-200 border-b border-gray-200 border-l-[3px] border-l-violet-300 bg-gray-50 hover:bg-gray-100/70 transition-colors";
    }

    // Ligne de coût ventilée : appartenance visuelle à la prestation parente.
    // Bordure gauche violette + bg violet très clair + bordures fines pour signaler
    // « ce n'est pas une ligne facturable indépendante, c'est un composant interne ».
    if (isCostLine) {
      return "group flex items-center border-b border-violet-100 border-l-[3px] border-l-violet-300 bg-violet-50/40 hover:bg-violet-50/70 transition-colors";
    }

    return "group flex items-center border-b border-gray-100 hover:bg-gray-50/80 transition-colors";
  };

  const renderConsultationIcon = () => {
    if (!consultationInfo) return <div className="w-8 flex-shrink-0" />;

    const { status, name, id, retainedCompanyName } = consultationInfo;
    const isDraft = !status || status === 'draft';
    const isSent = status === 'sent';

    const iconEl = isDraft
      ? <ClipboardDocumentListIcon className="w-4 h-4 text-gray-400" />
      : isSent
      ? <EnvelopeIcon className="w-4 h-4 text-orange-400" />
      : <CheckCircleIcon className="w-4 h-4 text-green-500" />;

    const tooltip = isDraft
      ? `Brouillon — ${name}`
      : isSent
      ? `Envoyée — ${name}`
      : `Attribuée à ${retainedCompanyName} — ${name}`;

    return (
      <div className="w-8 flex-shrink-0 flex items-center justify-center">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onOpenConsultation?.(id); }}
          className="relative group/consult w-6 h-6 flex items-center justify-center rounded hover:bg-gray-100 transition-colors"
          title={tooltip}
        >
          {iconEl}
          <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 whitespace-nowrap px-2 py-1 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 group-hover/consult:opacity-100 transition-opacity z-50">
            {tooltip}
          </span>
        </button>
      </div>
    );
  };

  const renderContent = () => {
    if (isPrestation(block)) {
      return (
        <>
          {block.level > 0 && <div className="flex-shrink-0" style={{ width: block.level * 20 }} />}
          <div className="flex-1 min-w-0 py-2.5 pr-3 overflow-hidden">
            <div className="flex items-center gap-1.5 min-w-0">
              {/* Symbole d'appartenance ↳ pour les lignes ventilées : signale
                  que la ligne est un composant interne d'une prestation parente
                  (pas une ligne facturable indépendante). Combiné au bg violet
                  clair + bordure gauche, lève toute ambiguïté de doublage. */}
              {isCostLine && (
                <span
                  className="text-violet-400 text-base leading-none flex-shrink-0 select-none"
                  title="Composant ventilé — fait partie de la prestation parente, ne s'ajoute pas au total"
                  aria-hidden="true"
                >
                  ↳
                </span>
              )}
              <EditableField
                value={block.content.intitule || ''}
                onSave={(value) => handleFieldSave('intitule', value)}
                onCancel={handleFieldCancel}
                placeholder="Nouvelle prestation"
                className={`text-sm font-medium ${isCostLine ? 'text-gray-700' : 'text-gray-900'}`}
              />
              {/* Badge F / F+P : visible uniquement si la ligne est associée à un
                  item de la base (matchedItemId). L'absence de badge signale à
                  l'utilisateur qu'il doit soit trouver une association biblio,
                  soit saisir manuellement un prix F+P (chiffrage libre). */}
              {block.content.matchedItemId && <FPLabel mode={decompositionMode} />}
            </div>
          </div>
          <div className="w-14 flex-shrink-0 py-2.5 px-1 text-center overflow-hidden">
            <EditableField
              value={block.content.unite || ''}
              onSave={(value) => handleFieldSave('unite', value)}
              onCancel={handleFieldCancel}
              placeholder="—"
              className="text-sm text-gray-500 text-center"
            />
          </div>
          <div className="w-16 flex-shrink-0 py-2.5 px-1 text-right overflow-hidden">
            <EditableField
              value={block.content.quantite || ''}
              onSave={(value) => handleFieldSave('quantite', value)}
              onCancel={handleFieldCancel}
              placeholder="—"
              className="text-sm text-gray-700 text-right"
            />
          </div>
          <div
            className="w-28 flex-shrink-0 py-2.5 pr-2"
            onContextMenu={(e) => {
              if (block.content.prixUnitaireSource === 'database' && block.content.prixUnitaireSources?.length) {
                e.preventDefault();
                e.stopPropagation();
                setShowPuModal(true);
              }
            }}
          >
            <div className="flex items-center justify-end gap-1">
              {isDetaillee ? (
                <span className="relative group/pu flex items-center gap-1">
                  <span className="inline-block w-2 h-2 rounded-full flex-shrink-0 bg-blue-500" />
                  <EditableField
                    value={block.content.prixUnitaire || ''}
                    onSave={(value) => handleFieldSave('prixUnitaire', value)}
                    onCancel={handleFieldCancel}
                    className="text-sm text-right text-violet-600"
                    readOnly
                    readOnlyTitle="PU composé depuis la décomposition (F + MO + Appro/qté)"
                  />
                  <span className="pointer-events-none absolute bottom-full right-0 mb-2 w-52 opacity-0 group-hover/pu:opacity-100 transition-opacity duration-150 z-50">
                    <span className="block bg-gray-900 text-white text-xs rounded-lg px-3 py-2.5 shadow-xl">
                      <span className="flex items-center gap-1 mb-1 text-blue-400 font-semibold">
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-400" />
                        PU composé
                      </span>
                      <span className="text-gray-400">Calculé depuis la décomposition (F + MO + Appro/qté) — modifiez les champs du bandeau pour ajuster</span>
                    </span>
                    <span className="absolute top-full right-3 border-4 border-transparent border-t-gray-900" />
                  </span>
                </span>
              ) : block.content.prixUnitaireExpertCurated ? (
                <span className="relative group/pu flex items-center gap-1">
                  <span className="inline-block w-2 h-2 rounded-full flex-shrink-0 bg-violet-500" />
                  <EditableField
                    value={block.content.prixUnitaire || ''}
                    onSave={(value) => handleFieldSave('prixUnitaire', value)}
                    onCancel={handleFieldCancel}
                    className="text-sm text-right text-violet-600"
                    readOnly={isDetaillee}
                    readOnlyTitle="PU calculé par la décomposition F/MO — modifiez les champs du bandeau ambre"
                  />
                  <span className="pointer-events-none absolute bottom-full right-0 mb-2 w-48 opacity-0 group-hover/pu:opacity-100 transition-opacity duration-150 z-50">
                    <span className="block bg-gray-900 text-white text-xs rounded-lg px-3 py-2.5 shadow-xl">
                      <span className="flex items-center gap-1 mb-1 text-violet-400 font-semibold">
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-violet-400" />
                        Prix personnalisé
                      </span>
                      <span className="text-gray-400">Références ajustées manuellement — le PU reflète votre sélection</span>
                      {block.content.prixUnitaireSources && block.content.prixUnitaireSources.filter(s => s.active).length > 0 && (
                        <span className="block mt-1.5 pt-1.5 border-t border-gray-700 text-gray-400 text-[10px] italic">
                          Clic droit → modifier la sélection
                        </span>
                      )}
                    </span>
                    <span className="absolute top-full right-3 border-4 border-transparent border-t-gray-900" />
                  </span>
                </span>
              ) : block.content.prixUnitaireReliability ? (() => {
                const rel = block.content.prixUnitaireReliability!;
                const cfg = RELIABILITY_DOT[rel];
                const textColor = rel === 'high' ? 'text-green-400' : rel === 'medium' ? 'text-orange-400' : 'text-red-400';
                const activeSources = block.content.prixUnitaireSources?.filter(s => s.active) ?? [];
                const hasSources = activeSources.length > 0;
                let statsEl: React.ReactNode = null;
                if (hasSources) {
                  const prices = activeSources.map(s => s.prixUnitaire).sort((a, b) => a - b);
                  const sum = prices.reduce((a, b) => a + b, 0);
                  const avg = sum / prices.length;
                  const mid = Math.floor(prices.length / 2);
                  const median = prices.length % 2 === 0 ? (prices[mid - 1] + prices[mid]) / 2 : prices[mid];
                  const fmt = (n: number) => BlockCalculationService.formatPrice(n);
                  const unite = block.content.unite ?? '';
                  statsEl = (
                    <>
                      <span className="space-y-1 block text-gray-300 mt-1.5">
                        <span className="flex justify-between"><span>Min</span><span className="tabular-nums">{fmt(prices[0])} €/{unite}</span></span>
                        <span className="flex justify-between"><span>Max</span><span className="tabular-nums">{fmt(prices[prices.length - 1])} €/{unite}</span></span>
                        <span className="flex justify-between font-semibold text-white"><span>Moy.</span><span className="tabular-nums">{fmt(avg)} €/{unite}</span></span>
                        <span className="flex justify-between"><span>Médiane</span><span className="tabular-nums">{fmt(median)} €/{unite}</span></span>
                      </span>
                      <span className="flex justify-between items-center mt-1.5 pt-1.5 border-t border-gray-700 text-gray-400 text-[10px]">
                        <span>{activeSources.length} référence{activeSources.length > 1 ? 's' : ''}</span>
                        <span className="italic">Clic droit → détails</span>
                      </span>
                    </>
                  );
                }
                return (
                  <span className="relative group/pu flex items-center gap-1">
                    <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${cfg.dot}`} />
                    <EditableField
                      value={block.content.prixUnitaire || ''}
                      onSave={(value) => handleFieldSave('prixUnitaire', value)}
                      onCancel={handleFieldCancel}
                      className={`text-sm text-right ${getPrixOriginColor(getPrixOriginState(block.content.prixUnitaireSource, block.content.prixUnitaireMatchType))}`}
                      readOnly={isDetaillee}
                      readOnlyTitle="PU calculé par la décomposition F/MO"
                    />
                    <span className="pointer-events-none absolute bottom-full right-0 mb-2 w-52 opacity-0 group-hover/pu:opacity-100 transition-opacity duration-150 z-50">
                      <span className="block bg-gray-900 text-white text-xs rounded-lg px-3 py-2.5 shadow-xl">
                        <span className={`flex items-center gap-1 mb-1 ${textColor} font-semibold`}>
                          <span className={`inline-block w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                          {cfg.label}
                        </span>
                        <span className="text-gray-400 text-[10px] block mb-1.5">{cfg.reason}</span>
                        <span className="font-medium text-gray-100 block mb-0.5 truncate">
                          {block.content.intitule || block.content.titre}
                        </span>
                        {statsEl}
                      </span>
                      <span className="absolute top-full right-3 border-4 border-transparent border-t-gray-900" />
                    </span>
                  </span>
                );
              })() : (
                <EditableField
                  value={block.content.prixUnitaire || ''}
                  onSave={(value) => handleFieldSave('prixUnitaire', value)}
                  onCancel={handleFieldCancel}
                  className={`text-sm text-right ${getPrixOriginColor(getPrixOriginState(block.content.prixUnitaireSource, block.content.prixUnitaireMatchType))}`}
                  readOnly={isDetaillee}
                  readOnlyTitle="PU calculé par la décomposition F/MO"
                />
              )}
            </div>
          </div>
          <div className="w-14 flex-shrink-0 py-2.5 px-1">
            <CoefField
              value={block.content.coefLigne}
              defaultValue={decompositionParams?.coefDefaut}
              onChange={handleCoefChange}
            />
          </div>
          <div className="w-28 flex-shrink-0 py-2.5 pr-2">
            <div className="flex items-center justify-end gap-1 font-semibold text-violet-600">
              {block.content.prixTotalManual && (
                <span
                  title="Total saisi manuellement — changez la quantité ou le prix unitaire pour recalculer automatiquement"
                  className="text-amber-400 text-sm"
                >✎</span>
              )}
              <EditableField
                value={block.content.prixTotal || '0'}
                onSave={(value) => handleFieldSave('prixTotal', value)}
                onCancel={handleFieldCancel}
                className="text-sm text-right text-violet-600"
                readOnly={isDetaillee}
                readOnlyTitle="Total calculé par la décomposition F/MO"
              />
              <span className="text-xs text-violet-500">€</span>
            </div>
          </div>
          {renderConsultationIcon()}
          {/* Colonne ventilation — trigger icône. Visible en vue détaillée et
              ventilée ; masquée en vue client (export). La colonne (w-8)
              est conservée pour préserver l'alignement. */}
          <div className="w-8 flex-shrink-0 flex items-center justify-center">
            {chiffrageView !== 'client' && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onToggleVentilation?.(block.id); }}
                className="relative w-6 h-6 flex items-center justify-center rounded hover:bg-gray-100 transition-colors"
                title={isVentilationExpanded ? 'Masquer la ventilation' : 'Afficher la ventilation'}
              >
                {(hasCostLines || !!block.content.matchedItemId) && (
                  <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-violet-500" />
                )}
                <AdjustmentsHorizontalIcon className={`w-3.5 h-3.5 transition-colors ${isVentilationExpanded ? 'text-violet-600' : 'text-gray-300 group-hover:text-gray-400'}`} />
              </button>
            )}
          </div>
          {/* Colonne décomposition — trigger icône (piste 2 / modèle A).
              Bouton toggle : click bascule entre forfaitaire et fourni-pose
              et replie/déplie la subbar inline. Violet quand actif, gris
              discret (hover only) quand inactif. Désactivé quand la ligne
              est ventilée (mutex avec ventilation). Visible uniquement en
              vue détaillée — la vue client masque tout, la vue ventilée
              montre la ventilation mais pas le détail F/MO interne. */}
          <div className="w-8 flex-shrink-0 flex items-center justify-center">
            {!hasCostLines && decompositionParams && chiffrageView === 'detaillee' && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  const willExpand = !isDecompositionExpanded;
                  onToggleDecomposition?.(block.id);
                  // À l'ouverture d'une ligne sans décomposition, activer fourni-pose
                  // pour que la subbar ait quelque chose à éditer. Sinon on préserve
                  // le mode en place (expansion/collapse pur).
                  if (willExpand && !isDetaillee) {
                    onUpdate(block.id, {
                      content: { ...block.content, decompositionMode: 'fourni-pose' },
                    });
                  }
                }}
                className="relative w-6 h-6 flex items-center justify-center rounded hover:bg-gray-100 transition-colors"
                title={isDecompositionExpanded ? 'Masquer la décomposition' : 'Afficher la décomposition F / MO / Appro'}
              >
                {isDetaillee && (
                  <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-violet-500" />
                )}
                <Squares2X2Icon className={`w-3.5 h-3.5 transition-colors ${isDecompositionExpanded ? 'text-violet-600' : 'text-gray-300 group-hover:text-gray-400'}`} />
              </button>
            )}
          </div>
          <div className="w-14 flex-shrink-0 flex items-center justify-end pr-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <BlockActionsComponent
              block={block}
              onDelete={() => onDelete(block.id)}
            />
          </div>
        </>
      );
    } else {
      const sectionTotal = allBlocks
        ? BlockCalculationService.calculatePrestationTotal(block, allBlocks.filter(b => !b.content.isVentilationCostLine))
        : 0;

      return (
        <>
          {block.level > 0 && <div className="flex-shrink-0" style={{ width: block.level * 20 }} />}
          <div className="flex-1 min-w-0 py-3 flex items-center gap-2">
            {block.children.length > 0 && (
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleCollapse?.(block.id); }}
                className="flex-shrink-0 w-5 h-5 inline-flex items-center justify-center rounded hover:bg-gray-200 text-gray-500 hover:text-gray-700"
                aria-label={block.isCollapsed ? 'Afficher les enfants' : 'Masquer les enfants'}
                title={block.isCollapsed ? 'Développer' : 'Réduire'}
              >
                <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${block.isCollapsed ? '-rotate-90' : ''}`} />
              </button>
            )}
            <EditableField
              value={block.content.titre}
              onSave={(value) => onUpdate(block.id, { content: { ...block.content, titre: value } })}
              placeholder="Nouveau lot"
              className="text-sm font-semibold text-gray-700"
            />
            {block.children.length > 0 && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-500">
                {block.children.length}
              </span>
            )}
            {lotEvents && lotEvents.length > 0 && (
              <LotEventBadge
                events={lotEvents}
                onPointEvent={onPointEvent}
                onOpenCreateEvent={onOpenCreateEvent}
              />
            )}
          </div>
          <div className="w-14 flex-shrink-0" />
          <div className="w-16 flex-shrink-0" />
          <div className="w-28 flex-shrink-0" />
          <div className="w-14 flex-shrink-0" />
          <div className="w-28 flex-shrink-0 py-3 pr-2">
            <div className="flex items-center justify-end gap-1">
              <span className="text-sm font-semibold text-violet-600">{BlockCalculationService.formatPrice(sectionTotal)}</span>
              <span className="text-xs text-violet-400">€</span>
            </div>
          </div>
          {renderConsultationIcon()}
          <div className="w-8 flex-shrink-0" />
          <div className="w-8 flex-shrink-0" />
          <div className="w-14 flex-shrink-0 flex items-center justify-end pr-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <BlockActionsComponent
              block={block}
              onDelete={() => onDelete(block.id)}
            />
          </div>
        </>
      );
    }
  };

  return (
    <>
    <div
      className={getBlockStyles()}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      tabIndex={0}
      role="button"
      aria-label={isPrestation(block)
        ? `Bloc prestation: ${block.content.intitule}`
        : `Bloc section: ${block.content.titre}`}
    >
      {/* Checkbox */}
      <div className="w-8 flex-shrink-0 flex items-center justify-center pl-2">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => {}}
          onClick={(e) => {
            e.stopPropagation();
            onToggleSelection?.(block.id, e.shiftKey);
          }}
          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
        />
      </div>

      {/* Drag handle */}
      <div
        data-drag-handle
        className="w-5 flex-shrink-0 flex items-center justify-center cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 select-none"
        onMouseDown={handleDragStart}
        onClick={handleDragHandleClick}
        title="Glisser pour déplacer"
      >
        ≡
      </div>

      {renderContent()}
    </div>

    {/* Accordion ventilation panel — masqué en vue client (export). */}
    {isPrestation(block) && isVentilationExpanded && chiffrageView !== 'client' && allBlocks && onVentilationBlocksChange && (
      <VentilationRowPanel
        block={block}
        allBlocks={allBlocks}
        onBlocksChange={onVentilationBlocksChange}
        decompositionParams={decompositionParams}
      />
    )}

    {/* Décomposition F/MO — bandeau inline (piste 2 / modèle A) :
        visible uniquement quand mode !== 'forfaitaire'. Pas d'accordéon,
        pas de résumé cliquable — le bandeau lui-même est l'éditeur. Le
        trigger d'ouverture est le lien « décomposer » dans la cellule PU.
        La fermeture (×) repasse en forfaitaire (les valeurs F/MO/appro
        sont conservées pour permettre une réactivation rapide).
        Désactivé quand la prestation est ventilée (mutex) : la décomposition
        descend alors au niveau de chaque ligne fille via VentilationCostRow.
        Visible uniquement en vue détaillée — la vue client et la vue ventilée
        masquent ce détail F/MO interne (le PU/total persistent sur la ligne). */}
    {isPrestation(block) && !hasCostLines && decompositionParams && isDecompositionExpanded && isDetaillee && chiffrageView === 'detaillee' && (
      <DecompositionInlineSubbar
        block={block}
        params={decompositionParams}
        onContentChange={(nextContent) => {
          const applied = DecompositionCalculationService.isDetailleeContent(nextContent)
            ? DecompositionCalculationService.applyBreakdownToContent(
                nextContent,
                nextContent.quantite,
                decompositionParams,
              )
            : nextContent;
          onUpdate(block.id, { content: applied });
        }}
        onClose={() => {
          onUpdate(block.id, {
            content: { ...block.content, decompositionMode: 'forfaitaire' },
          });
          onToggleDecomposition?.(block.id);
        }}
      />
    )}

    {showPuModal && (
      <PuSourcesModal
        block={block}
        onClose={() => setShowPuModal(false)}
        onUpdate={(updates) => onUpdate(block.id, updates)}
      />
    )}
    </>
  );
};
